import type { CSSProperties, ReactNode } from 'react'
import { formatCores, type SizeValues } from '../../core/sizing'
import { formatStopTime, type RunTimeReadout } from '../../core/runTime'

/*
  The pieces both review screens are built from (RENT-RUNTIME-PLAN W1, W5).

  The posted-price review and the open-request review describe the same
  escrow. They used to be written twice, and the second copy is how the two
  screens come to say different things about the same money. A change to the
  prepay notice, the escrow line or the readout is made here, once.
*/

/** CPU, memory and storage as three tiles; the summary text when no size is known. */
export function ResourceTiles({ size, resources }: { size: SizeValues | undefined; resources: string }) {
  if (!size) return <p className="rent-review-resources">{resources}</p>
  const tiles: Array<[string, string]> = [['CPU', formatCores(size.cpu)], ['Memory', `${size.ram} GB`], ['Storage', `${size.storage} GB`]]
  return <dl className="resource-tiles" aria-label="Resources">
    {tiles.map(([label, value]) => <div key={label} className="resource-tile"><dt>{label}</dt><dd>{value}</dd></div>)}
  </dl>
}

/** The high-contrast notice under "How long should it run?". The words that were missing for the beta tenant. */
export function PrepayNotice() {
  return <p className="notice notice-strong" role="note">
    You prepay for run time. When it runs out, the lease closes and your instance stops. You can top up any time from the instance page.
  </p>
}

/**
 * The deposit and refund facts, as one quiet line rather than two bold rows.
 * An open request pays whichever provider is accepted, and has a refund case
 * a direct rental does not: nobody offers.
 */
export function EscrowSteps({ openRequest = false }: { openRequest?: boolean }) {
  return <ol className="runtime-steps" aria-label="How the prepaid run time is paid">
    <li>Held in escrow, not a fee</li>
    <li>{openRequest ? 'Paid to the provider you accept, block by block' : 'Paid to the provider block by block'}</li>
    <li>{openRequest ? 'Whatever is unused comes back when you close. No offer? Close the request to get it all back' : 'Whatever is unused comes back when you close'}</li>
  </ol>
}

/**
 * "Runs for about 1 day · Stops around …" with its log-scale bar. `qualifier`
 * replaces "about" where the figure is a floor (the open request's highest
 * price); `children` is what follows a short run on that screen.
 */
export function RunTimeReadoutView({ readout, qualifier = 'about', suffix, children }: { readout: RunTimeReadout; qualifier?: 'about' | 'at least'; suffix?: string | undefined; children?: ReactNode }) {
  return <div className={`runtime-readout${readout.short ? ' runtime-readout--short' : ''}`} aria-live="polite">
    <p className="runtime-readout-duration">Runs for {qualifier} <strong>{readout.duration}</strong>{suffix ? ` ${suffix}` : ''}</p>
    <p className="runtime-readout-stop">Stops around {formatStopTime(readout.stopsAt)}{qualifier === 'at least' ? ' at the earliest' : ''}</p>
    <span className="runtime-bar" role="img" aria-label={`Run time on a scale from 1 hour to 30 days: ${qualifier} ${readout.duration}`} style={{ '--fill': `${Math.round(readout.bar * 100)}%` } as CSSProperties} />
    {children}
  </div>
}
